/**
 * Reusable collapsible group for AI Block executions.
 *
 * - Pass `actionFilter` (e.g. 'accepted', 'rejected', 'flagged_review') to
 *   show only runs with that action, themed by action color.
 * - Omit `actionFilter` to show ALL runs with a per-action count breakdown.
 *   The aggregated net-changes table always uses accepted runs only.
 */

import React, { useMemo } from 'react'
import { Icon, type IconName } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import _ from 'lodash'
import type { IAiBlockActivityOptions, ExtendedBlockEvent } from './ai-block-activity-types'
import { AiBlockExecutionCard } from './ai-block-execution-card'
import { formatRunTimestamp, computeFieldRows } from './ai-block-activity-helpers'
import { FieldsTableView, type FieldTableRowData } from './fields-table-view'

export interface NumberedExecution {
  execution: ExtendedBlockEvent
  runNumber: number
}

const ACTION_THEME: Record<string, { label: string; variant: string; icon: IconName }> = {
  accepted: { label: 'Accepted', variant: 'green', icon: IconNames.TICK_CIRCLE },
  rejected: { label: 'Rejected', variant: 'red', icon: IconNames.CROSS },
  flagged_review: { label: 'Flagged for Review', variant: 'amber', icon: IconNames.FLAG },
}

interface IAiBlockActivityGroupProps {
  options: IAiBlockActivityOptions
  runs: NumberedExecution[]
  actionFilter?: string
  expanded: boolean
  onToggle: () => void
}

export const AiBlockActivityGroup: React.FC<IAiBlockActivityGroupProps> = ({
  options,
  runs,
  actionFilter,
  expanded,
  onToggle,
}) => {
  // Filter runs if actionFilter is provided
  const displayRuns = useMemo(() => {
    if (!actionFilter) return runs
    return runs.filter(({ execution }) => execution.result?.action === actionFilter)
  }, [runs, actionFilter])

  // Theme
  const theme = actionFilter ? ACTION_THEME[actionFilter] : null
  const variant = theme?.variant || 'neutral'
  const icon = theme?.icon || IconNames.LAYERS

  // Per-action counts (used when no filter)
  const actionCounts = useMemo(() => {
    return _.countBy(displayRuns, ({ execution }) => execution.result?.action || 'unknown')
  }, [displayRuns])

  // Net changes always come from accepted runs
  const acceptedForChanges = useMemo(() => {
    return displayRuns.filter(({ execution }) => execution.result?.action === 'accepted')
  }, [displayRuns])

  const aggregatedFields: FieldTableRowData[] = useMemo(() => {
    if (acceptedForChanges.length === 0) return []
    const fieldMap = new Map<string, FieldTableRowData>()

    // Process from oldest to newest: first previousValue, last currentValue and confidence
    for (let i = acceptedForChanges.length - 1; i >= 0; i--) {
      const { execution } = acceptedForChanges[i]
      const rows = computeFieldRows(
        options,
        execution.result?.changeSet,
        execution.result?.extractedFields,
      )
      for (const row of rows) {
        const existing = fieldMap.get(row.fieldPath)
        if (!existing) {
          fieldMap.set(row.fieldPath, {
            fieldPath: row.fieldPath,
            fieldName: row.fieldName,
            previousValue: row.previousValue,
            currentValue: row.currentValue,
            confidence: row.confidence,
            explanation: row.explanation,
          })
        } else {
          // Update to most recent change's values and confidence
          existing.currentValue = row.currentValue
          existing.confidence = row.confidence
          existing.explanation = row.explanation
        }
      }
    }
    return Array.from(fieldMap.values())
  }, [acceptedForChanges, options])

  // Header title
  const headerTitle = useMemo(() => {
    const count = displayRuns.length
    const runWord = count === 1 ? 'Run' : 'Runs'
    if (actionFilter && theme) {
      return `${count} ${theme.label} ${runWord}`
    }
    const parts = Object.entries(actionCounts).map(([action, c]) => {
      const config = ACTION_THEME[action]
      return `${c} ${config?.label || _.startCase(action)}`
    })
    return `${count} ${runWord} (${parts.join(', ')})`
  }, [actionFilter, theme, displayRuns.length, actionCounts])

  if (displayRuns.length === 0) return null

  const totalTokens = displayRuns.reduce(
    (sum, { execution }) => sum + (execution.tokenUsage?.total || 0),
    0,
  )
  const newest = displayRuns[0].execution
  const oldest = displayRuns[displayRuns.length - 1].execution

  return (
    <div className={`c-aiBlockResults-activityGroup c-aiBlockResults-activityGroup--${variant}`}>
      <div className='c-aiBlockResults-activityGroup-header' onClick={onToggle}>
        <div className='c-aiBlockResults-activityGroup-headerLeft'>
          <Icon icon={icon} iconSize={16} className='c-aiBlockResults-activityGroup-icon' />
          <span className='c-aiBlockResults-activityGroup-title'>{headerTitle}</span>
          {displayRuns.length > 1 && (
            <span className='c-aiBlockResults-activityGroup-dateRange'>
              {formatRunTimestamp(oldest.timing?.receivedDate || oldest.creationDate)}
              {' — '}
              {formatRunTimestamp(newest.timing?.receivedDate || newest.creationDate)}
            </span>
          )}
        </div>
        <div className='c-aiBlockResults-activityGroup-headerRight'>
          {totalTokens > 0 && (
            <span className='c-aiBlockResults-activityGroup-tokens'>
              {totalTokens.toLocaleString()} tokens
            </span>
          )}
          <Icon
            icon={expanded ? IconNames.CHEVRON_UP : IconNames.CHEVRON_DOWN}
            iconSize={16}
            className='c-aiBlockResults-activityGroup-chevron'
          />
        </div>
      </div>

      {/* Aggregated net changes (accepted only) — shown when collapsed */}
      {aggregatedFields.length > 0 && !expanded && (
        <div className='c-aiBlockResults-activityGroup-changesBody'>
          <FieldsTableView
            rows={aggregatedFields}
            showOperation={false}
            previousValueLabel='Original Value'
            currentValueLabel='Current Value'
          />
        </div>
      )}

      {expanded && (
        <div className='c-aiBlockResults-activityGroup-body'>
          {displayRuns.map(({ execution, runNumber }) => (
            <AiBlockExecutionCard
              key={execution.creationDate + runNumber}
              options={options}
              execution={execution}
              runNumber={runNumber}
            />
          ))}
        </div>
      )}
    </div>
  )
}
